"use client";

import {
  CalendarCheck,
  CheckCheck,
  MailOpen,
  MousePointerClick,
  Reply,
  UserPlus,
} from "lucide-react";

const pct = (n: number) => `${Math.round(n)}%`;
import { type BlastStep, BlastFeed, type Campaign } from "./blast-scene";
import {
  FeatureUseCases,
  type SceneProps,
  type UseCase,
  useRevealCount,
} from "../_shared/use-cases-section";

/* ------------------------------------------------------------------ */
/* 1 · Win back the people who drifted off                            */
/* ------------------------------------------------------------------ */

const WINBACK_CAMPAIGN: Campaign = { name: "Win-back promo", channel: "Text · 1,240 recipients" };

const WINBACK: BlastStep[] = [
  {
    kind: "audience",
    id: "w1",
    name: "Haven't visited in 30 days",
    count: "1,240",
    filters: ["No visit 30+ days", "Has opted in"],
  },
  {
    kind: "message",
    id: "w2",
    caption: "Sent to 1,240",
    body: "We miss you! 💛 Your next class is on us this week — tap to grab your spot.",
  },
  {
    kind: "metrics",
    id: "w4",
    items: [
      { icon: CheckCheck, label: "Delivered", to: 1212, suffix: " / 1,240" },
      { icon: MousePointerClick, label: "Clicks", to: 318 },
      { icon: Reply, label: "Replies", to: 87 },
      { icon: CalendarCheck, label: "Booked", to: 41 },
    ],
  },
  {
    kind: "reply",
    id: "w5",
    direction: "incoming",
    person: { name: "Jordan Hayes" },
    body: "Count me in! 🙌",
  },
  {
    kind: "reply",
    id: "w6",
    direction: "outgoing",
    body: "Love it, you're booked for Thursday 6pm 💪",
  },
];

function WinbackScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, WINBACK.length, reduce, active);
  return (
    <BlastFeed
      campaign={WINBACK_CAMPAIGN}
      steps={WINBACK}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Fill the empty spots in tomorrow's class                       */
/* ------------------------------------------------------------------ */

const FILL_CAMPAIGN: Campaign = { name: "Last-minute openings", channel: "Text · 86 recipients" };

const FILL: BlastStep[] = [
  {
    kind: "audience",
    id: "f1",
    name: "Tomorrow's 6pm regulars",
    count: "86",
    filters: ["Books Tue 6pm", "Not booked yet"],
  },
  {
    kind: "message",
    id: "f2",
    caption: "Sent to 86",
    body: "3 spots just opened in tomorrow's 6pm Reformer. First to reply grabs one!",
  },
  {
    kind: "metrics",
    id: "f4",
    items: [
      { icon: CheckCheck, label: "Delivered", to: 86, suffix: " / 86" },
      { icon: MousePointerClick, label: "Clicks", to: 39 },
      { icon: Reply, label: "Replies", to: 22 },
      { icon: CalendarCheck, label: "Filled", to: 3, suffix: " / 3" },
    ],
  },
  {
    kind: "reply",
    id: "f5",
    direction: "incoming",
    person: { name: "Tasha Bell" },
    body: "Me! Can I grab one?",
  },
  {
    kind: "reply",
    id: "f6",
    direction: "outgoing",
    body: "You're in for 6pm 🙌 See you there!",
  },
];

function FillScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, FILL.length, reduce, active);
  return (
    <BlastFeed
      campaign={FILL_CAMPAIGN}
      steps={FILL}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Cast a wide net for a big promo                                */
/* ------------------------------------------------------------------ */

const PROMO_CAMPAIGN: Campaign = {
  name: "New Year promo",
  channel: "Text + email · 4,820 recipients",
};

const PROMO: BlastStep[] = [
  {
    kind: "audience",
    id: "g1",
    name: "Everyone on your list",
    count: "4,820",
    filters: ["Leads + customers", "Text, email fallback"],
  },
  {
    kind: "message",
    id: "g2",
    caption: "Text",
    body: "New year, new you 💪 2 weeks unlimited for $39. This week only, tap to claim.",
  },
  {
    kind: "email",
    id: "g2b",
    caption: "Email fallback · not opted in to text",
    brand: "Barre3 West Loop",
    logo: "/images/barre3-icon.png",
    image: "/images/barre3-lobby.jpg",
    subject: "New year, new you. 2 weeks unlimited for $39.",
    cta: "Claim my offer",
  },
  {
    kind: "metrics",
    id: "g4",
    items: [
      { icon: CheckCheck, label: "Delivered", to: 4690, suffix: " / 4,820" },
      { icon: MailOpen, label: "Opens", to: 61, format: pct },
      { icon: MousePointerClick, label: "Clicks", to: 902 },
      { icon: UserPlus, label: "Signups", to: 96 },
    ],
  },
  {
    kind: "reply",
    id: "g5",
    direction: "incoming",
    person: { name: "Marcus Reed" },
    body: "Just claimed it, so excited to start!",
  },
  {
    kind: "reply",
    id: "g6",
    direction: "outgoing",
    body: "Welcome! 🎉 Your first class is Saturday at 9am.",
  },
];

function PromoScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, PROMO.length, reduce, active);
  return (
    <BlastFeed
      campaign={PROMO_CAMPAIGN}
      steps={PROMO}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

const CASES: UseCase[] = [
  {
    id: "winback",
    title: "Win back the people drifting away",
    description:
      "Target everyone who hasn't visited in a month and send one warm nudge. Replies land back in your inbox, ready to book.",
    Scene: WinbackScene,
    steps: WINBACK.length,
  },
  {
    id: "fill",
    title: "Fill every open spot, fast",
    description:
      "Text just the regulars who fit an open class and let the first replies claim the spots. Empty slots fill in minutes.",
    Scene: FillScene,
    steps: FILL.length,
  },
  {
    id: "promo",
    title: "Cast a wide net for big promos",
    description:
      "Reach your whole list by text with an email fallback, then watch opens, clicks, and signups climb in real time.",
    Scene: PromoScene,
    steps: PROMO.length,
  },
];

export function BlastsUseCases() {
  return (
    <FeatureUseCases
      title={
        <>
          One message. Real results.
          <span className="text-muted-foreground">
            {" "}
            See how studios fill classes, win people back, and launch big promos in minutes.
          </span>
        </>
      }
      cases={CASES}
    />
  );
}
